import React from 'react';
import { Coins, Trash2, User, Pause, Loader2 } from 'lucide-react';
import { ScanMode } from '../types';

interface CartItem {
  ean: string;
  name: string;
  price: number;
  quantity: number;
}

interface CartViewProps {
  cart: CartItem[];
  scanMode: ScanMode;
  isSuspendingCart: boolean;
  onCheckout: () => void;
  onRemoveItem: (index: number) => void;
  onSuspend: () => void;
}

export const CartView: React.FC<CartViewProps> = ({
  cart,
  scanMode,
  isSuspendingCart,
  onCheckout,
  onRemoveItem,
  onSuspend,
}) => {
  const totalAmount = cart.reduce((sum, item) => sum + item.price * item.quantity, 0);

  // Get mode label and color
  const getModeInfo = (mode: ScanMode) => {
    switch (mode) {
      case 'SALE':
        return { label: 'ELADÁS', color: 'green' };
      case 'RETURN':
        return { label: 'VISSZÁRU', color: 'purple' };
      case 'SCRAP':
        return { label: 'SELEJT', color: 'red' };
      case 'INTERNAL':
        return { label: 'SAJÁT FELHASZNÁLÁS', color: 'orange' };
      default:
        return { label: 'KOSÁR', color: 'green' };
    }
  };

  const modeInfo = getModeInfo(scanMode);
  const headerBgColor =
    modeInfo.color === 'green'
      ? 'bg-green-50 dark:bg-green-900/20'
      : modeInfo.color === 'purple'
        ? 'bg-purple-50 dark:bg-purple-900/20'
        : modeInfo.color === 'red'
          ? 'bg-red-50 dark:bg-red-900/20'
          : 'bg-orange-50 dark:bg-orange-900/20';
  const headerTextColor =
    modeInfo.color === 'green'
      ? 'text-green-800 dark:text-green-200'
      : modeInfo.color === 'purple'
        ? 'text-purple-800 dark:text-purple-200'
        : modeInfo.color === 'red'
          ? 'text-red-800 dark:text-red-200'
          : 'text-orange-800 dark:text-orange-200';
  const iconColor =
    modeInfo.color === 'green'
      ? 'text-green-600 dark:text-green-400'
      : modeInfo.color === 'purple'
        ? 'text-purple-600 dark:text-purple-400'
        : modeInfo.color === 'red'
          ? 'text-red-600 dark:text-red-400'
          : 'text-orange-600 dark:text-orange-400';
  const buttonBgColor =
    modeInfo.color === 'green'
      ? 'bg-green-600 hover:bg-green-700'
      : modeInfo.color === 'purple'
        ? 'bg-purple-600 hover:bg-purple-700'
        : modeInfo.color === 'red'
          ? 'bg-red-600 hover:bg-red-700'
          : 'bg-orange-600 hover:bg-orange-700';
  const totalTextColor =
    modeInfo.color === 'green'
      ? 'text-green-600 dark:text-green-400'
      : modeInfo.color === 'purple'
        ? 'text-purple-600 dark:text-purple-400'
        : modeInfo.color === 'red'
          ? 'text-red-600 dark:text-red-400'
          : 'text-orange-600 dark:text-orange-400';
  const footerBgColor =
    modeInfo.color === 'green'
      ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
      : modeInfo.color === 'purple'
        ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
        : modeInfo.color === 'red'
          ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
          : 'bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800';

  return (
    <div className="bg-card rounded-xl shadow-lg border border-slate-200 dark:border-gray-700 overflow-hidden">
      <div
        className={`px-4 md:px-6 py-3 border-b border-slate-200 dark:border-gray-700 ${headerBgColor} flex items-center justify-between`}
      >
        <div className="flex items-center gap-2">
          <Coins size={20} className={iconColor} />
          <h3 className={`font-bold ${headerTextColor} text-lg`}>Kosár ({modeInfo.label})</h3>
        </div>
        {cart.length > 0 && (
          <button
            onClick={onCheckout}
            className={`${buttonBgColor} text-white px-6 py-2 rounded-lg font-bold transition shadow-md flex items-center gap-2`}
          >
            <User size={18} />
            Fizetés (vagy USER kártya)
          </button>
        )}
      </div>

      {cart.length === 0 ? (
        <div className="p-12 text-center text-slate-400 dark:text-gray-500">
          <Coins size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">A kosár üres</p>
          <p className="text-sm mt-2">Szkenneld be a termékeket a kosárhoz adáshoz</p>
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100 dark:divide-gray-700 max-h-[400px] overflow-y-auto">
            {cart.map((item, index) => {
              const total = item.price * item.quantity;
              return (
                <div
                  key={`${item.ean}-${index}`}
                  className="px-4 md:px-6 py-4 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-gray-700/50 transition"
                >
                  <div className="flex-1 min-w-0 pr-4">
                    <p className="font-bold text-gray-900 dark:text-white text-lg truncate">
                      {item.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-mono mt-1">
                      {item.ean}
                    </p>
                    <div className="flex items-center gap-4 mt-2">
                      <span className="text-sm text-gray-600 dark:text-gray-300">
                        {item.quantity} db × {item.price.toLocaleString('hu-HU')} Ft
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 shrink-0">
                    <div className="text-right">
                      <p className={`font-bold ${totalTextColor} text-xl`}>
                        {total.toLocaleString('hu-HU')} Ft
                      </p>
                    </div>
                    <button
                      onClick={() => onRemoveItem(index)}
                      className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition"
                      title="Törlés"
                    >
                      <Trash2 size={20} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className={`px-4 md:px-6 py-4 ${footerBgColor} border-t space-y-3`}>
            <div className="flex items-center justify-between">
              <span className="text-lg font-bold text-gray-700 dark:text-gray-300">Összesen:</span>
              <span className={`text-3xl font-extrabold ${totalTextColor}`}>
                {totalAmount.toLocaleString('hu-HU')} Ft
              </span>
            </div>
            <button
              onClick={onSuspend}
              disabled={isSuspendingCart}
              className="w-full py-2 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white rounded-lg font-semibold transition shadow-md flex items-center justify-center gap-2"
              title="Kosár parkolása későbbi folytatáshoz"
            >
              {isSuspendingCart ? (
                <>
                  <Loader2 className="animate-spin" size={18} />
                  Parkolás...
                </>
              ) : (
                <>
                  <Pause size={18} />
                  Parkolás
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};
